import { same, type Json } from './readEdit';

/**
 * Autonomous light program of one device, in human values (user request 2026-10-12: "the editor uses human values, not
 * masks"). The form is protocol-free: the gateway reads and writes it with the capabilities light_program.read /
 * light_program.write and translates it to the device's own settings
 * (docs/architecture/17-autonomous-light-programs-proposal.md).
 * - bands: brightest first, each up to a boundary in lux, the last one without a boundary; level 0 = off;
 * - tolerance: dead band around each boundary, so the light does not flicker on its own reflection;
 * - sensor hours: when the light sensor decides; outside them the level stays or follows the timed steps;
 * - steps: time → level on chosen days;
 * - power-on level (null when the device does not report one).
 */
export const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export type Day = (typeof DAYS)[number];
export type Band = { up_to_lux?: number; level_pct: number };
export type Step = { id: number; time: string; level_pct: number; days: Day[]; enabled: boolean };
export type LightProgram = {
  sensor: { enabled: boolean; from: string; to: string };
  bands: Band[];
  tolerance_lux: number;
  steps: Step[];
  power_on_level_pct: number | null;
};
export type ProgramLimits = { bands: number; steps: number; max_lux: number };
export type ProgramNotices = { steps_not_running: number[]; approximated: boolean };
export type ProgramRead = { program: LightProgram; limits: ProgramLimits; notices: ProgramNotices };

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
const pct = (n: number) => Number.isInteger(n) && n >= 0 && n <= 100;
const asObj = (v: unknown): Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : {};

/** The read answer (light_program.read result) in typed form; missing parts get neutral values. */
export function parseProgramRead(result: unknown): ProgramRead {
  const r = asObj(result);
  const p = asObj(r.program);
  const s = asObj(p.sensor);
  const l = asObj(r.limits);
  const n = asObj(r.notices);
  const bands = (Array.isArray(p.bands) ? p.bands : []).map((b) => {
    const o = asObj(b);
    return typeof o.up_to_lux === 'number'
      ? { up_to_lux: o.up_to_lux, level_pct: Number(o.level_pct ?? 0) }
      : { level_pct: Number(o.level_pct ?? 0) };
  });
  const steps = (Array.isArray(p.steps) ? p.steps : []).map((x) => {
    const o = asObj(x);
    const days = Array.isArray(o.days) ? (o.days as string[]) : [];
    return {
      id: Number(o.id),
      time: String(o.time ?? ''),
      level_pct: Number(o.level_pct ?? 0),
      days: DAYS.filter((d) => days.includes(d)),
      enabled: o.enabled !== false,
    };
  });
  return {
    program: {
      sensor: { enabled: s.enabled === true, from: String(s.from ?? '00:00'), to: String(s.to ?? '00:00') },
      bands,
      tolerance_lux: Number(p.tolerance_lux ?? 0),
      steps,
      power_on_level_pct: typeof p.power_on_level_pct === 'number' ? p.power_on_level_pct : null,
    },
    limits: {
      bands: Number(l.bands ?? bands.length),
      steps: Number(l.steps ?? 0),
      max_lux: Number(l.max_lux ?? 65535),
    },
    notices: {
      steps_not_running: Array.isArray(n.steps_not_running) ? (n.steps_not_running as number[]) : [],
      approximated: n.approximated === true,
    },
  };
}

const canonical = (p: LightProgram): Json =>
  ({ ...p, steps: [...p.steps].sort((a, b) => a.id - b.id) }) as unknown as Json;

/** How many parts of the program differ (sensor, each band, tolerance, each step, power-on level). */
export function programChanges(before: LightProgram, after: LightProgram): number {
  let n = 0;
  if (!same(before.sensor as unknown as Json, after.sensor as unknown as Json)) n++;
  after.bands.forEach((b, i) => {
    if (!same(b as unknown as Json, before.bands[i] as unknown as Json)) n++;
  });
  if (before.tolerance_lux !== after.tolerance_lux) n++;
  const ids = new Set([...before.steps, ...after.steps].map((s) => s.id));
  for (const id of ids) {
    const a = before.steps.find((s) => s.id === id);
    const b = after.steps.find((s) => s.id === id);
    if (!same(a as unknown as Json, b as unknown as Json)) n++;
  }
  if (before.power_on_level_pct !== after.power_on_level_pct) n++;
  return n;
}

export const sameProgram = (a: LightProgram, b: LightProgram) => same(canonical(a), canonical(b));

export type ProgramError =
  | 'time'
  | 'boundaryOrder'
  | 'boundaryRange'
  | 'tolerance'
  | 'level'
  | 'stepTime'
  | 'stepDays'
  | 'tooManySteps';

/** The same rules the gateway applies, so mistakes show while typing. */
export function validateProgram(p: LightProgram, limits: ProgramLimits): ProgramError[] {
  const e = new Set<ProgramError>();
  if (!HHMM.test(p.sensor.from) || !HHMM.test(p.sensor.to)) e.add('time');
  const t = p.tolerance_lux;
  if (!Number.isInteger(t) || t < 1) e.add('tolerance');
  let prevUp = 0;
  p.bands.forEach((b, i) => {
    if (!pct(b.level_pct)) e.add('level');
    if (i === p.bands.length - 1) return;
    const up = b.up_to_lux ?? NaN;
    // a return threshold of 0 never brings the brighter band back
    if (!Number.isInteger(up) || up - t < 1 || up + t > limits.max_lux) e.add('boundaryRange');
    else if (i > 0 && up - t <= prevUp) e.add('boundaryOrder');
    prevUp = up + t;
  });
  if (p.power_on_level_pct !== null && !pct(p.power_on_level_pct)) e.add('level');
  if (p.steps.length > limits.steps) e.add('tooManySteps');
  for (const s of p.steps) {
    if (!HHMM.test(s.time)) e.add('stepTime');
    if (!pct(s.level_pct)) e.add('level');
    if (!s.days.length) e.add('stepDays');
  }
  return [...e];
}

/** A free id for a new step (ids 1..limit). */
export const nextStepId = (steps: Step[], limit: number) =>
  Array.from({ length: limit }, (_, i) => i + 1).find((n) => !steps.some((s) => s.id === n));
